import { Modal, Space, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { Key, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import AbaTypeEnum from '~Api/WarehouseLoanTransactionAba/AbaTypeEnum';
import { IGlobalState } from '~reducer';
import SelectModalFooter from '~UI/SelectModalFooter';
import { currencyFormatter } from '~utilities/formatters';
import ICheckboxProps from '~utilities/ICheckboxProps';
import { IDictionary } from '~utilities/IDictionary';
import { abasAddAction, unallocatedTransactionsListAction } from '~WarehouseLoanTransactionAbas/actions';
import { unallocatedTransactionsSelector } from '~WarehouseLoanTransactionAbas/selectors';

interface IGroupedTransaction {
    allSelected?: boolean;
    amountPrincipal: number;
    children?: IGroupedTransaction[];
    loanCode: string;
    loanUuid: string;
    someSelected?: boolean;
    transactionTime: string;
    uuid: string;
}

const titles: IDictionary<string> = {
    [AbaTypeEnum.FinSalePrincipal]: 'Add FIN Transfer',
    [AbaTypeEnum.FitSalePrincipal]: 'Add FIT Transfer',
    [AbaTypeEnum.FnomSalePrincipal]: 'Add FNOM Transfer',
};

export default function AddModal(props: {
    abaType: AbaTypeEnum;
    isOpen: boolean;
    onClose: () => void;
}): ReactElement {
    const { abaType, isOpen, onClose } = props;

    const [ selectedRowKeys, setSelectedRowKeys ] = useState<Key[]>(null);

    const unallocatedTransactions: IDictionary<IWarehouseLoanTransaction> = useSelector((state: IGlobalState): IDictionary<IWarehouseLoanTransaction> => unallocatedTransactionsSelector(state, abaType));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!unallocatedTransactions) {
            dispatch(unallocatedTransactionsListAction(abaType));
        }
    }, [
        abaType,
        dispatch,
        unallocatedTransactions,
    ]);

    const columns: ColumnsType<IGroupedTransaction> = [
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IGroupedTransaction): ReactNode => <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>,
            title: 'Loan',
        },
        {
            dataIndex: 'transactionTime',
            defaultSortOrder: 'descend',
            render: (transactionTime: string, row: IGroupedTransaction): ReactNode => !row.children && transactionTime && dayjs(transactionTime).format('DD/MM/YYYY'),
            sorter: (a: IGroupedTransaction, b: IGroupedTransaction): number => dayjs(a.transactionTime) > dayjs(b.transactionTime) ? 1 : -1,
            title: 'Date',
            width: 200,
        },
        {
            dataIndex: 'amountPrincipal',
            render: (amountPrincipal: number, row: IGroupedTransaction): ReactNode => {
                return (
                    <Space>
                        <>{currencyFormatter.format(amountPrincipal)}</>
                        {row.children && <Tag>&sum;</Tag>}
                    </Space>
                );
            },
            title: 'Principal',
            width: 200,
        },
    ];

    const onChangeSelectedRows: (rowKeys: Key[], rows: IGroupedTransaction[]) => void = useCallback((rowKeys: Key[], rows: IGroupedTransaction[]): void => {
        setSelectedRowKeys(rowKeys);
    }, []);

    const getCheckboxProps: (row: IGroupedTransaction) => ICheckboxProps = useCallback((row: IGroupedTransaction): ICheckboxProps => ({
        className: null,
        disabled: false,
        key: row.uuid,
    }), []);

    const allRowKeys: Key[] = [];
    const groupedTransactions: IDictionary<IGroupedTransaction> = {};

    // Group all items
    _.each(unallocatedTransactions, (unallocatedTransaction: IWarehouseLoanTransaction): void => {
        if (!groupedTransactions[unallocatedTransaction.loanCode]) {
            groupedTransactions[unallocatedTransaction.loanCode] = {
                amountPrincipal: 0,
                children: [],
                loanCode: unallocatedTransaction.loanCode,
                loanUuid: unallocatedTransaction.loanUuid,
                transactionTime: unallocatedTransaction.transactionTime,
                uuid: unallocatedTransaction.loanCode,
            };
        }

        groupedTransactions[unallocatedTransaction.loanCode].children.push(unallocatedTransaction);

        allRowKeys.push(unallocatedTransaction.uuid);
    });

    // Degroup items with 1 child
    _.each(groupedTransactions, (groupedTransaction: IGroupedTransaction, loanCode: string): void => {
        if (groupedTransaction.children.length > 1) {
            allRowKeys.push(loanCode);

            return;
        }

        groupedTransactions[loanCode] = groupedTransaction.children[0];
    });

    const actuallySelectedRowKeys: Key[] = selectedRowKeys || allRowKeys;

    // Set group totals
    _.each(groupedTransactions, (groupedTransaction: IGroupedTransaction, loanCode: string): void => {
        if (!groupedTransaction.children) {
            return;
        }

        _.each(groupedTransaction.children, (childTransaction: IGroupedTransaction): void => {
            if (!actuallySelectedRowKeys.includes(childTransaction.uuid)) {
                return;
            }

            groupedTransactions[loanCode].amountPrincipal += childTransaction.amountPrincipal;
        });
    });

    const selectedUuids: string[] = useMemo((): string[] => {
        const result: string[] = [];
        _.each(actuallySelectedRowKeys, (actuallySelectedRowKey: Key): void => {
            if (actuallySelectedRowKey.toString().startsWith('FL-')) {
                return;
            }

            result.push(actuallySelectedRowKey.toString());
        });

        return result;
    }, [
        actuallySelectedRowKeys,
    ]);

    const onOk: () => void = useCallback((): void => {
        dispatch(abasAddAction(abaType, selectedUuids));
        onClose();
    }, [
        abaType,
        dispatch,
        onClose,
        selectedUuids,
    ]);

    const rowSelection: TableRowSelection<IGroupedTransaction> = {
        checkStrictly: false,
        getCheckboxProps,
        onChange: onChangeSelectedRows,
        selectedRowKeys: actuallySelectedRowKeys,
    };

    const total: number = _.reduce(
        selectedUuids,
        (sum: number, selectedRowKey: Key): number => sum + unallocatedTransactions[selectedRowKey.toString()].amountPrincipal,
        0,
    );

    const footer: ReactNode = (
        <SelectModalFooter
            count={selectedUuids.length}
            onCancel={onClose}
            onOk={onOk}
            total={total}
        />
    );

    return (
        <Modal
            footer={footer}
            onCancel={onClose}
            open={isOpen}
            title={titles[abaType]}
            width={1000}
        >
            <Table
                columns={columns}
                dataSource={_.values(groupedTransactions)}
                loading={!unallocatedTransactions}
                pagination={false}
                rowKey='uuid'
                rowSelection={rowSelection}
                scroll={{ y: 500 }}
                size='middle'
            />
        </Modal>
    );
}
